import React, { useEffect } from "react";
import { DatePicker } from "@mui/x-date-pickers";

import { useField } from "formik";

import { TextField, TextFieldProps, FormControl, InputLabel, OutlinedInput, FormHelperText } from "@mui/material";
import MaskedInput from "react-text-mask";
import { omit } from "lodash";
import createNumberMask from "text-mask-addons/dist/createNumberMask";
import { toProperCase } from "pages/dashboard/interests/utils";

export const CURRENCY_MASK = createNumberMask({ prefix: "$", allowDecimal: true });

type DatePickerProps = Omit<React.ComponentProps<typeof DatePicker>, "onChange" | "value"> & {
  id?: string;
  withError?: boolean;
  small?: boolean;
  name: string;
  margin?: "none" | "dense" | "normal";
  fullWidth?: boolean;

  /** Runs after Formik blur (e.g. re-validate sibling fields). */
  afterBlur?: () => void;

  /** Runs after the value updates (next tick), for cross-field validation. */
  afterValueChange?: () => void;
};

// eslint-disable-next-line max-lines-per-function -- MUI DatePicker + Formik wiring
const DatePickerField: React.FC<DatePickerProps> = (props) => {
  const { withError, small, label, fullWidth, afterBlur, afterValueChange, ...otherProps } = props;
  const [field, meta, helpers] = useField(props.name);
  const { name, value } = field;
  const { touched, error } = meta;
  const { setValue, setTouched, setError } = helpers;
  return (
    <DatePicker
      {...otherProps}
      slotProps={{
        textField: {
          // `fullWidth` belongs on the underlying TextField; the DatePicker
          // itself ignores it, so forward it through slotProps.
          fullWidth,
          style: { minWidth: "15em" },
          error: touched && !!error,
          margin: small ? "dense" : "normal",
          id: `hc-date-${name}`,
          name,
          autoComplete: "off",
          variant: "outlined",
          label: label ?? "",
          helperText: withError && touched && error,
          onBlur: (event: React.FocusEvent<HTMLInputElement>) => {
            field.onBlur(event);
            afterBlur?.();
          },
        },
        inputAdornment: { position: "end" },
      }}
      format="MM/dd/yyyy"
      value={value}
      closeOnSelect
      slots={{ textField: TextField }}
      onOpen={() => setTouched(true)}
      onClose={() => {
        // Defer so a date chosen via closeOnSelect commits before we re-validate.
        // Validating synchronously here runs against Formik's stale (pre-select)
        // value, which reintroduces a just-cleared error.
        window.setTimeout(() => {
          setTouched(true);
          afterBlur?.();
        }, 0);
      }}
      onChange={(date: Date | null) => {
        if (date && isNaN(date.getDay())) {
          setError("Invalid Date");
        } else {
          setValue(date);
          if (afterValueChange) {
            window.setTimeout(() => {
              afterValueChange();
            }, 0);
          }
        }
      }}
    />
  );
};

export type InputWithErrorLabelProps = {
  label?: string;
  name: string;
} & TextFieldProps;

const Input: React.FC<InputWithErrorLabelProps> = (props) => (
  <TextField
    fullWidth
    margin="dense"
    id={`hc-text-${props.name}`}
    {...props}
  />
);

const InputWithErrorLabel: React.FC<InputWithErrorLabelProps> = (props) => {
  const { name, margin = "dense", ...inputProps } = props;
  const label = props.title ?? props.label ?? toProperCase(props.name);
  const [field, meta, helpers] = useField(name);
  // Validate on transition:
  useEffect(() => () => {
    helpers.setTouched(true);
  }, []);

  const helperText = (meta.touched && meta.error) || inputProps.helperText;
  return (
    <TextField
      id={`hc-text-${name}`}
      label={label}
      {...field}
      fullWidth
      margin={margin}
      {...inputProps}
      error={meta.touched && !!meta.error}
      helperText={helperText}
    />
  );
};

const MaskedInputControl: React.FC<any> = (inputProps) => {
  const other = omit(inputProps, ["inputRef"]);
  return (
    <MaskedInput
      {...other}
      guide={inputProps.guide}
      mask={inputProps.mask}
      placeholderChar={"\u2000"}
    />);
};

const MaskedInputWithErrorLabel: React.FC<InputWithErrorLabelProps & {mask: any, guide?: boolean}> =
(props) => {
  const { name, label, required, margin = "dense" } = props;
  const [field, meta, helpers] = useField(name);
  // Validate on transition:
  useEffect(() => () => {
    helpers.setTouched(true);
  }, []);

  return (
    <FormControl
      fullWidth
      variant="outlined"
      margin={margin}
      required={required}
      error={meta.touched && !!meta.error}
    >
      <InputLabel htmlFor={`${name}-outlined`} shrink required={required}>
        {label}
      </InputLabel>
      <OutlinedInput
        {...field}
        label={label}
        notched
        margin={margin === "normal" ? "none" : margin}
        id={`${name}-outlined`}
        inputComponent={MaskedInputControl}
        inputProps={{
          mask: props.mask,
          guide: props.guide,
        }}
        placeholder={props.placeholder}
        required={required}
      />

      {meta.touched && meta.error && (
        <FormHelperText className="color-hc-error">
          {meta.error}
        </FormHelperText>
      )}
    </FormControl>
  );
};


export {
  Input,
  InputWithErrorLabel,
  MaskedInputControl,
  MaskedInputWithErrorLabel,
  DatePickerField,
};
